import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Mail, Plus, X } from "lucide-react";
import { API, fmtDate } from "./shared";

const inputCls =
  "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480] w-full";

const KINDS = [
  ["manual", "Manual message"],
  ["summary", "Appointment summary"],
  ["invoice", "Invoice"],
  ["reminder", "Reminder"],
  ["education", "Educational resources"],
];

export function EmailsTab({ authHeader }) {
  const [history, setHistory] = useState(null);
  const [to, setTo] = useState([""]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [template, setTemplate] = useState("branded");
  const [kind, setKind] = useState("manual");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/emails`, { headers: authHeader() });
      setHistory(res.data);
    } catch { setHistory([]); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendOne = async (e) => {
    setBusy(true);
    setMsg("");
    try {
      const res = await axios.post(`${API}/admin/emails/${e.id}/resend`, {}, { headers: authHeader() });
      setMsg(res.data.status === "sent"
        ? `Re-sent to ${res.data.to} — delivered.`
        : `Re-send to ${res.data.to} attempted — delivery still failing while the email service is being restored.`);
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not re-send.");
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await axios.post(`${API}/admin/emails/send`, {
        to: to.map((t) => t.trim()).filter(Boolean),
        subject,
        body_html: body.replace(/\n/g, "<br>"),
        template,
        kind,
      }, { headers: authHeader() });
      const failed = res.data.results.filter((r) => r.status === "failed").length;
      setMsg(failed
        ? `Logged for ${res.data.results.length} recipient(s) — delivery failed while the email service is being restored. History is kept below.`
        : `Sent to ${res.data.results.length} recipient(s).`);
      setTo([""]);
      setSubject("");
      setBody("");
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="emails-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Email Center</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Send branded emails to one client or many — summaries, invoices, reminders, resources. Every send is logged with its delivery status.
        </p>
      </div>

      <form onSubmit={send} data-testid="email-compose" className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Recipients</div>
        <div className="space-y-2 mb-4">
          {to.map((addr, i) => (
            <div key={i} className="flex gap-2">
              <input type="email" required placeholder="client@email.com" value={addr}
                data-testid={`email-to-${i}`}
                onChange={(e) => setTo(to.map((t, j) => (j === i ? e.target.value : t)))}
                className={inputCls} />
              {to.length > 1 && (
                <button type="button" onClick={() => setTo(to.filter((_, j) => j !== i))}
                  data-testid={`email-to-remove-${i}`}
                  className="p-2 rounded-full text-[#875F45] hover:bg-[#875F45]/10" aria-label="Remove recipient">
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          {to.length < 50 && (
            <button type="button" onClick={() => setTo([...to, ""])} data-testid="email-add-recipient"
              className="text-xs text-[#3F5043] font-semibold inline-flex items-center gap-1 hover:text-[#875F45]">
              <Plus size={12} /> Add another recipient
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <select value={kind} data-testid="email-kind" onChange={(e) => setKind(e.target.value)} className={inputCls}>
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={template} data-testid="email-template" onChange={(e) => setTemplate(e.target.value)} className={inputCls}>
            <option value="branded">Branded Soarlife template</option>
            <option value="plain">Plain (no branding)</option>
          </select>
        </div>
        <input required placeholder="Subject" value={subject} data-testid="email-subject"
          onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mb-3`} />
        <textarea required rows={7} placeholder="Write your message… (line breaks are preserved; basic HTML like <b>bold</b> works)"
          value={body} data-testid="email-body" onChange={(e) => setBody(e.target.value)}
          className={`${inputCls} resize-y mb-4`} />
        <div className="flex items-center gap-3 flex-wrap">
          <button type="submit" data-testid="email-send-btn" disabled={busy}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send email
          </button>
          {msg && <span className="text-xs text-[#875F45]" data-testid="email-msg">{msg}</span>}
        </div>
      </form>

      <div className="text-[11px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Email history</div>
      {!history ? (
        <div className="text-center py-8"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : history.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#899480]" data-testid="emails-empty">
          No emails sent yet.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#EDEDEE] text-left text-[10px] uppercase tracking-[0.15em] text-[#899480]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="email-history">
              {history.map((e) => (
                <tr key={e.id} data-testid={`email-row-${e.id}`} className="border-t border-[#BCB3A6]/20">
                  <td className="px-4 py-2.5 text-[#3F5043]/70 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-2.5 text-[#3F5043]">{e.to}</td>
                  <td className="px-4 py-2.5 text-[#3F5043]/80">{e.subject}</td>
                  <td className="px-4 py-2.5 text-[#899480] capitalize">{e.kind}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-0.5 rounded-full ${
                      e.status === "sent" ? "bg-[#899480]/15 text-[#3F5043]" : "bg-red-100 text-red-700"}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {e.status === "failed" && e.resendable && (
                      <button onClick={() => resendOne(e)} disabled={busy} data-testid={`email-resend-${e.id}`}
                        className="text-xs rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60">
                        Resend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
